import { Fragment } from "react";
import { signal, useSignal } from "@preact/signals-react";

import proxy from "api/proxy";
import { QrCodeIcon } from "components/Icons";
import mainStore from "store/mainStore";
import BarcodeReader from "componentsv2/widgets/BarcodeReader";
import Button from "componentsv2/button/Button";
import { CustomModal } from "componentsv2/modal/CustomModal";
import ProductItemCard from "./ProductItemCard";

const showModal = signal(false);
const alertMsg = signal({
  show: false,
  text: null,
  icon: "check",
  color: "bg-green-200",
  position: "fixed",
  className: "bottom-0 max-w-[600px] left-0 right-0 !z-50",
  autoRemove: true,
});
const code = signal(null);
let seqCounter = 0;

function AddProductBarcode() {
  const records = useSignal([]);
  const { store: storeRecord, record, updateRecord, updateStore } = mainStore();

  const updateQuantity = (_, value) => {
    const updated = [...records.value];
    for (const key in value) {
      const idx = Number(key);
      const qty = Number(value[key]);
      if (updated[idx]) updated[idx].quantity = qty;
    }
    records.value = updated;
  };

  const fieldQuantity = {
    label: "stock.shipment.internal.quantity",
    name: "quantity",
    withChange: updateQuantity,
  };

  const getData = async (codeValue) => {
    if (!codeValue) return;

    showModal.value = true;

    const existing = records.value.find((item) => item.code === codeValue);

    if (existing) {
      records.value = records.value.map((item) =>
        item.code === codeValue
          ? { ...item, quantity: Number(item.quantity) + 1 }
          : item,
      );

      alertMsg.value = {
        ...alertMsg.value,
        show: true,
        text: `Cantidad aumentada para ${codeValue}`,
        icon: "check",
        color: "bg-green-200",
      };

      return;
    }

    const { data: res } = await proxy.search(
      "product.product",
      [["code", "=", codeValue]],
      [
        "id",
        "list_price",
        "name",
        "code",
        "sale_price_w_tax",
        "sale_uom.rec_name",
        "default_uom.name",
      ],
    );

    if (res.length === 0) {
      alertMsg.value = {
        show: true,
        text: "Producto no encontrado",
        icon: "xmark",
        color: "bg-red-300",
      };
      return;
    }

    const product = res[0];

    const existingMove = Array.from(
      mainStore.getState().record.moves?.values() || [],
    ).find(
      (m) => m.product?.id === product.id || m.product?.code === product.code,
    );

    const baseQty = existingMove ? existingMove.quantity + 1 : 1;

    records.value = [
      ...records.value,
      {
        id: product.id,
        name: product.name,
        code: product.code,
        price: product.list_price,
        uom: product["default_uom."],
        quantity: baseQty,
      },
    ];

    alertMsg.value = {
      ...alertMsg.value,
      show: true,
      text: `Producto ${product.name} agregado`,
      icon: "check",
      color: "bg-green-200",
    };

    code.value = null;
  };

  const addLineShipmenpInternal = async () => {
    const { store: freshStore, record: freshRecord } = mainStore.getState();
    const _storeRecord = { ...freshStore };
    const _activeRecord = { ...freshRecord };
    const _records = Array.isArray(records.value) ? [...records.value] : [];

    if (!_records.length) return false;

    let moveMap;
    if (_storeRecord.moves instanceof Map) {
      moveMap = new Map(_storeRecord.moves);
    } else if (_storeRecord.moves && typeof _storeRecord.moves === "object") {
      moveMap = new Map(Object.entries(_storeRecord.moves));
    } else {
      moveMap = new Map();
    }

    let createObj = moveMap.get("create");
    if (!(createObj instanceof Map)) createObj = new Map();

    let writeObj = moveMap.get("write");
    if (!(writeObj instanceof Map)) writeObj = new Map();

    let deleteArr = moveMap.get("delete");
    if (!Array.isArray(deleteArr)) deleteArr = [];

    const newMoves = new Map(_activeRecord.moves || []);

    for (const product of _records) {
      const existingEntry = Array.from(newMoves.entries()).find(
        ([id, move]) =>
          move.product?.id === product.id ||
          move.product?.id === product.product?.id,
      );

      if (existingEntry) {
        const [existingId, existingMove] = existingEntry;
        existingMove.quantity = product.quantity ?? 1;

        if (createObj.has(existingId)) {
          createObj.get(existingId).quantity = existingMove.quantity;
        }
        if (writeObj.has(existingId)) {
          writeObj.get(existingId).quantity = existingMove.quantity;
        }
      } else {
        const newSeq = -(Date.now() + seqCounter++);

        const lineRecord = {
          id: newSeq,
          product,
          quantity: product.quantity ?? 1,
          unit_price: product?.list_price ?? 0,
          unit: product?.uom,
          from_location: _activeRecord?.from_location,
          to_location: _activeRecord?.to_location,
        };

        const lineStore = {
          id: newSeq,
          product: product.id,
          quantity: product.quantity ?? 1,
          unit_price: product?.list_price ?? 0,
          unit: product?.uom?.id,
          from_location: _activeRecord?.from_location?.id,
          to_location: _activeRecord?.to_location?.id,
        };

        newMoves.set(newSeq, lineRecord);
        createObj.set(newSeq, lineStore);
      }
    }

    moveMap.set("create", createObj);
    moveMap.set("write", writeObj);
    moveMap.set("delete", deleteArr);

    updateStore({ ..._storeRecord, moves: moveMap });
    updateRecord({ ..._activeRecord, moves: newMoves });
    showModal.value = false;
    records.value = [];

    // showModal.value = false;

    return true;
  };

  function ContentModal() {
    function onRemove(item) {
      records.value = records.value.filter((r) => r.id !== item.id);
    }

    return (
      <div className="w-full relative min-h-300px] h-full mt-5">
        <div className="w-full mt-2 h-full">
          <div className="space-y-6">
            {records.value.map((item, index) => (
              <ProductItemCard
                key={index}
                item={item}
                index={index}
                fieldQuantity={fieldQuantity}
                onRemove={onRemove}
              />
            ))}
          </div>

          {records.value.length > 0 && (
            <div className="relative top-2">
              <Button
                onClick={addLineShipmenpInternal}
                content={"AGREGAR PRODUCTOS"}
                disabled={() => records.value.length === 0}
                color="amber"
                style="!w-max"
                size="w-max"
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <button
        type="button"
        className="bg-yellow-500 text-blue-800 px-6 !py-4 flex items-center justify-center space-x-3 rounded-md shadow-md hover:scale-95 transition-all duration-150 cursor-pointer mx-auto w-full right-0 bottom-0 col-span-2 max-w-fit my-10 text-xl"
        onClick={() => (showModal.value = true)}
      >
        <QrCodeIcon className="w-8 fill-current text-black" />
        <span className="text-black">ACTIVAR MODO BARCODE</span>
      </button>

      <CustomModal
        openModal={showModal}
        handleClose={() => (showModal.value = false)}
        title="Agregar productos"
        size="w-full"
        style="max-w-[800px]"
        styleChild="pb-0! min-h-[300px]!"
      >
        <ContentModal onClose={() => (showModal.value = false)} />
        <BarcodeReader data={code} handleReader={getData} />
      </CustomModal>
    </Fragment>
  );
}

export default AddProductBarcode;
